import * as z from 'zod'

import {queries} from '@/shared/integrations/zero'
import {SortSchema} from '@cypro/ui/schemas'
import {VolumeMode, PatientSource, DoseCalcModeActive} from '@cypro/engine/database/schema'
import {QueryResultType} from '@rocicorp/zero'
import {MedicineSchema} from '../protocols/schemas'
import {Gender, Sex} from '@cypro/engine/database/schema'
import {PatientProtocol} from '@cypro/engine/queries'
import {getFirstCycleStartDate, CarrierSolutionValidationSchema} from '@cypro/engine/utils'

export const PatientFormSchema = z.object({
  title: z.array(z.string()).nonempty(),
  givenName: z.string().trim().nonempty(),
  surname: z.string().trim().nonempty(),
  sex: z.array(Sex).nonempty(),
  gender: z.array(Gender),
  birthDate: z.array(z.any()).nonempty(),
  email: z.string(),
  phone: z.string(),
  country: z.array(z.string()),
  state: z.string(),
  city: z.string(),
  streetAddress: z.string(),
  postalCode: z.string(),
  sectionId: z.array(z.string()),
  wardId: z.array(z.string()),
  familyDoctorId: z.array(z.string()),
  responsibleDoctorId: z.array(z.string()),
  startedAt: z.any(),
  endedAt: z.any(),
  reason: z.string(),
  patientClass: z.array(z.string()),
  admissionType: z.array(z.string()),
  insuranceNumber: z.string(),
  nationalId: z.string(),
  hospitalPatientId: z.string(),
  barcodePrintFlag: z.boolean(),
  addMore: z.boolean(),
})

export type PatientTitle = 'mr' | 'mrs' | 'ms' | 'miss' | 'dr' | 'prof' | 'rev'

export const patientClasses = ['inpatient', 'outpatient', 'emergency', 'observation'] as const
export type PatientClass = (typeof patientClasses)[number]

export const admissionTypes = ['emergency', 'urgent', 'elective', 'newborn', 'trauma'] as const
export type AdmissionType = (typeof admissionTypes)[number]

export type PatientDetailed = QueryResultType<typeof queries.patients.listPatients>[number]
export type PatientExtended = QueryResultType<typeof queries.patients.getPatientById> & {id: string}

export const PatientFiltersSchema = z.object({
  search: z.string().optional(),
  responsibleDoctor: z.array(z.uuidv7()).default([]),
  source: z.array(PatientSource).default([PatientSource.enum.manual, PatientSource.enum.hl7_import]),
})

export const PatientSearchSchema = z.object({
  filters: PatientFiltersSchema.optional(),
  sort: z.array(SortSchema).default([{id: 'createdAt', desc: true}]),
  forbidden: z.boolean().optional(),
})

export const InstructionNoteSchema = z.object({
  patientProtocolId: z.array(z.string()),
  fixedDate: z.any(),
  cycle: z.number().optional(),
  day: z.number(),
  time: z.string().regex(/^(?:[01]\d|2[0-3]):[0-5]\d$/, 'Time must be in HH:MM format (00:00 to 23:59)'),
  isNote: z.boolean(),
  note: z.string(),
  recurringCycle: z.boolean().optional(),
  applyToFutureNotes: z.boolean().optional(),
})

export type InstructionNote = z.infer<typeof InstructionNoteSchema>

export const getInstructionNoteSchema = (patientProtocols: PatientProtocol[]) =>
  InstructionNoteSchema.superRefine((value, ctx) => {
    if (value.isNote && (!value.note || value.note.trim().length === 0)) {
      ctx.addIssue({
        code: 'custom',
        path: ['note'],
        message: 'notes_required',
      })
    }

    // The date is always picked; for a protocol note the cycle/day are derived from it.
    if (!value.fixedDate?.[0]) {
      ctx.addIssue({
        code: 'custom',
        path: ['fixedDate'],
        message: 'date_required',
      })
      return
    }

    const patientProtocol = patientProtocols.find(pp => pp.id === value.patientProtocolId[0])
    const protocol = patientProtocol?.protocol
    if (!protocol) {
      return
    }
    const totalCycles = patientProtocol.cycles ?? protocol.totalCycles

    if (value.cycle === undefined || value.cycle < 1 || value.cycle > totalCycles) {
      ctx.addIssue({
        code: 'custom',
        path: ['cycle'],
        message: 'cycle_out_of_range',
      })
    }

    if (value.fixedDate[0].toDate('UTC').getTime() < getFirstCycleStartDate(patientProtocol)) {
      ctx.addIssue({
        code: 'custom',
        path: ['fixedDate'],
        message: 'date_before_protocol_start',
      })
    }
  })

export const MedicineSchemaNew = z.object({
  id: z.string().optional(),
  activeSubstanceId: z.string().optional(),
  prescribedNameId: z.string().optional(),
  autIdem: z.boolean().optional(),
  calculationMethod: DoseCalcModeActive.optional(),
  regularDose: z.number(),
  precisionDecimals: z.int().min(0),
  doseProportion: z.number().min(0).max(100).optional(),
})

export type MedicineSchemaNew = z.infer<typeof MedicineSchemaNew>

export const ParameterSchemaNew = z.object({
  id: z.string().optional(),
  patientProtocolId: z.string().optional(),
  responsibleDoctorId: z.string().optional(),
  fixedDate: z.any().optional(),
  cycle: z.number().optional(),
  day: z.number().min(0).optional(),
  time: z
    .string()
    .min(0)
    .regex(/^(?:[01]\d|2[0-3]):[0-5]\d$/, 'Time must be in HH:MM format (00:00 to 23:59)'),
  isNote: z.boolean(),
  carrierSolutionId: z.string().optional(),
  applicationFormId: z.string().nonempty(),
  duration: z.number(),
  volume: z.number().optional(),
  volumeMode: VolumeMode.optional(),
  medicine: z.array(MedicineSchemaNew),
  requireCarrierSolution: z.boolean().optional(),
  dayZeroProtocol: z.boolean().optional(),
  recurringCycle: z.boolean().optional(),
  autIdem: z.boolean().optional(),
  showPrescribingNames: z.boolean().optional(),
  therapyNote: z.string().optional(),
  pharmacyNote: z.string().optional(),
})

export type ParameterSchemaNew = z.infer<typeof ParameterSchemaNew>

export const getParameterSchemaNew = (patientProtocols: PatientProtocol[]) =>
  ParameterSchemaNew.superRefine((value, ctx) => {
    value.medicine.forEach((med, index) => {
      if (!med.activeSubstanceId) {
        ctx.addIssue({
          code: 'custom',
          path: [`medicine[${index}].activeSubstanceId`],
          message: 'active_substance_required',
        })
      }

      if (!med.calculationMethod) {
        ctx.addIssue({
          code: 'custom',
          path: [`medicine[${index}].calculationMethod`],
          message: 'calculation_method_required',
        })
      }

      if (!med.regularDose) {
        ctx.addIssue({
          code: 'custom',
          path: [`medicine[${index}].regularDose`],
          message: 'regular_dose_required',
        })
      }

      if (value.showPrescribingNames && !med.prescribedNameId) {
        ctx.addIssue({
          code: 'custom',
          path: [`medicine[${index}].prescribedNameId`],
          message: 'prescribed_name_required',
        })
      }
    })

    const carrierValidation = CarrierSolutionValidationSchema.safeParse({
      requireCarrierSolution: value.requireCarrierSolution,
      carrierSolutionId: value.carrierSolutionId,
      volume: value.volume,
      volumeMode: value.volumeMode,
    })
    if (!carrierValidation.success) {
      for (const issue of carrierValidation.error.issues) {
        ctx.addIssue({
          code: 'custom',
          path: issue.path,
          message: issue.message,
        })
      }
    }

    if (!value.patientProtocolId) {
      if (!value.fixedDate) {
        ctx.addIssue({
          code: 'custom',
          path: ['fixedDate'],
          message: 'date_required',
        })
      }
      return
    }

    const patientProtocol = patientProtocols.find(pp => pp.id === value.patientProtocolId)
    const protocol = patientProtocol?.protocol
    if (!protocol) {
      return
    }
    const totalCycles = patientProtocol.cycles ?? protocol.totalCycles

    if (value.cycle === undefined || value.cycle < 1 || value.cycle > totalCycles) {
      ctx.addIssue({
        code: 'custom',
        path: ['cycle'],
        message: 'cycle_out_of_range',
      })
    }

    if (value.fixedDate && value.fixedDate.toDate('UTC').getTime() < getFirstCycleStartDate(patientProtocol)) {
      ctx.addIssue({
        code: 'custom',
        path: ['fixedDate'],
        message: 'date_before_protocol_start',
      })
    }
  })

export const ParameterSchema = z
  .object({
    id: z.string().optional(),
    patientProtocolId: z.array(z.string()),
    fixedDate: z.any(),
    cycle: z.number().optional(),
    day: z.number().min(0),
    time: z
      .string()
      .min(0)
      .regex(/^(?:[01]\d|2[0-3]):[0-5]\d$/, 'Time must be in HH:MM format (00:00 to 23:59)'),
    isNote: z.boolean(),
    carrierSolutionId: z.array(z.string()),
    applicationFormId: z.array(z.string()).nonempty(),
    duration: z.number(),
    volume: z.number(),
    volumeMode: z.array(VolumeMode),
    medicine: z.array(MedicineSchema),
    requireCarrierSolution: z.boolean().optional(),
    dayZeroProtocol: z.boolean().optional(),
  })
  .superRefine((value, ctx) => {
    if (!value.dayZeroProtocol) {
      if (value.day < 1) {
        ctx.addIssue({
          code: 'custom',
          path: ['day'],
          message: 'Day cannot be less then 1',
        })
      }
    }
    if (value.patientProtocolId.length === 0 && value.fixedDate.length === 0) {
      ctx.addIssue({
        code: 'custom',
        path: ['fixedDate'],
        message: 'date_required',
      })
    }

    if (value.patientProtocolId.length > 0 && value.day < 0) {
      ctx.addIssue({
        code: 'custom',
        path: ['day'],
        message: 'day_required',
      })
    }

    value.medicine.forEach((med, index) => {
      if (med.activeSubstanceId.length === 0 && med.prescribedNameId.length === 0) {
        ctx.addIssue({
          code: 'custom',
          path: [`medicine[${index}].activeSubstanceId`],
          message: 'Either an active substance or prescribed name is required',
        })
      }
      if (med.calculationMethod.length === 0) {
        ctx.addIssue({
          code: 'custom',
          path: [`medicine[${index}].calculationMethod`],
          message: 'At least one calculation method is required',
        })
      }
    })

    if (
      !value.isNote &&
      value.applicationFormId.length !== 0 &&
      value.requireCarrierSolution &&
      value.carrierSolutionId.length === 0
    ) {
      ctx.addIssue({
        code: 'custom',
        path: ['carrierSolutionId'],
        message: 'A carrier solution is required',
      })
    }

    if (
      !value.isNote &&
      value.carrierSolutionId.length !== 0 &&
      value.requireCarrierSolution &&
      value.volumeMode.length === 0
    ) {
      ctx.addIssue({
        code: 'custom',
        path: ['volumeMode'],
        message: 'A volume mode is required',
      })
    }

    if (!value.isNote && value.carrierSolutionId.length !== 0 && value.volumeMode.length === 0) {
      ctx.addIssue({
        code: 'custom',
        path: ['volumeMode'],
        message: 'A volume mode is required',
      })
    }

    if (!value.isNote && value.carrierSolutionId.length !== 0 && value.volume <= 0) {
      ctx.addIssue({
        code: 'custom',
        path: ['volume'],
        message: 'Volume is required',
      })
    }
  })

export type ParameterSchema = z.infer<typeof ParameterSchema>

export const GlobalDoseReductionSchema = z.object({
  patientId: z.string(),
  doseReduction: z.number().min(0).max(100),
})

export type GlobalDoseReductionSchema = z.infer<typeof GlobalDoseReductionSchema>

export const IndividualDoseReductionSchema = z
  .object({
    patientId: z.string(),
    doseReductions: z.array(
      z.object({
        drugCatalogItemId: z.string(),
        name: z.string(),
        doseReduction: z.number().min(0).max(100).optional(),
      })
    ),
  })
  .superRefine((value, ctx) => {
    const allEmpty = value.doseReductions.every(d => !d.doseReduction)
    if (allEmpty) {
      value.doseReductions.forEach((_, i) => {
        ctx.addIssue({
          code: 'custom',
          path: [`doseReductions[${i}].doseReduction`],
          message: 'at_least_one_dose_reduction_required',
        })
      })
    }
  })

export type IndividualDoseReductionSchema = z.infer<typeof IndividualDoseReductionSchema>
